import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { BOOKING_STATUS, PAYMENT_STATUS, SOURCES, STATUS_LIST, SOURCE_LIST } from "@/lib/bookingConstants";
import { PaymentBadge, BookingStatusBadge } from "@/components/dashboard/StatusBadge";
import BookingFormDrawer from "@/components/reservations/BookingFormDrawer";
import BookingDetailsDrawer from "@/components/reservations/BookingDetailsDrawer";
import { FieldSelect } from "@/components/ui/field-select";
import { Plus, Search, Loader2, X, ChevronLeft, ChevronRight, Ticket } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";

const QUICK = [
  { k: "today", label: "Aujourd'hui" }, { k: "tomorrow", label: "Demain" },
  { k: "week", label: "Cette semaine" }, { k: "month", label: "Ce mois" },
];

export default function Reservations() {
  const { orgId, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [quick, setQuick] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [payment, setPayment] = useState(params.get("payment_status") || "");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsId, setDetailsId] = useState(params.get("open") || null);
  const limit = 25;

  useEffect(() => { if (params.get("open")) { setDetailsId(params.get("open")); } }, [params]);
  useEffect(() => {
    if (params.get("new") && can("bookings.create")) {
      setEditing(null); setFormOpen(true);
      params.delete("new"); setParams(params, { replace: true });
    }
  }, [params, can, setParams]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["bookings", orgId, quick, q, status, payment, source, page],
    queryFn: async () => (await api.get("/api/bookings", {
      params: { organization_id: orgId, quick: quick || undefined, q: q || undefined,
        status: status || undefined, payment_status: payment || undefined,
        source: source || undefined, limit, skip: page * limit },
    })).data,
    enabled: !!orgId,
  });

  const reset = () => { setQuick(""); setQ(""); setStatus(""); setPayment(""); setSource(""); setPage(0); setParams({}); };
  const bookings = data?.bookings || [];
  const total = data?.total || 0;

  return (
    <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Réservations</h1>
          <p className="mt-1 text-sm text-slate-500">{total} réservation{total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
        {can("finance.view") && <ExportButton kind="bookings" orgId={orgId} testid="reservations-export-button" />}
        {can("bookings.create") && (
          <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="new-reservation-button"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Nouvelle réservation
          </button>
        )}
        </div>
      </div>

      {/* Quick date filters */}
      <div className="mt-6 flex flex-wrap gap-1.5">
        {QUICK.map((f) => (
          <button key={f.k} onClick={() => { setQuick(quick === f.k ? "" : f.k); setPage(0); }} data-testid={`quick-filter-${f.k}`}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${quick === f.k ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid="reservations-search-input" placeholder="Référence, nom, téléphone..."
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <FieldSelect testId="filter-status" className="w-44" value={status || "ALL"} onChange={(v) => { setStatus(v === "ALL" ? "" : v); setPage(0); }}
          options={[{ value: "ALL", label: "Tout statut" }, ...STATUS_LIST.map((s) => ({ value: s, label: BOOKING_STATUS[s].label }))]} />
        <FieldSelect testId="filter-payment" className="w-44" value={payment || "ALL"} onChange={(v) => { setPayment(v === "ALL" ? "" : v); setPage(0); }}
          options={[{ value: "ALL", label: "Tout paiement" }, ...Object.entries(PAYMENT_STATUS).map(([k, v]) => ({ value: k, label: v.label }))]} />
        <FieldSelect testId="filter-source" className="w-44" value={source || "ALL"} onChange={(v) => { setSource(v === "ALL" ? "" : v); setPage(0); }}
          options={[{ value: "ALL", label: "Toute source" }, ...SOURCE_LIST.map((s) => ({ value: s, label: SOURCES[s] }))]} />
        {(quick || q || status || payment || source) && (
          <button onClick={reset} data-testid="reset-filters" className="flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /> Réinitialiser</button>
        )}
      </div>

      {/* Table (desktop) */}
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_0_rgba(15,23,42,0.04)]">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center" data-testid="reservations-error">
            <p className="text-sm text-slate-500">Impossible de charger les réservations.</p>
            <button onClick={() => refetch()} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50">Réessayer</button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="reservations-empty"><Ticket className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-3 text-base font-semibold text-slate-700">Aucune réservation</p><p className="mt-1 text-sm text-slate-400">Créez votre première réservation pour lancer votre activité.</p>{can("bookings.create") && <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="reservations-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle réservation</button>}</div>
        ) : (
          <>
            <table className="hidden w-full text-left md:table" data-testid="reservations-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50/80 text-xs font-semibold uppercase tracking-wider text-slate-500">
                {["Référence", "Date", "Heure", "Client", "Activité", "Pax", "Montant", "Paiement", "Statut", "Source"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} onClick={() => setDetailsId(b.id)} data-testid={`booking-row-${b.id}`} className="cursor-pointer border-b border-slate-100 text-sm transition-colors hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{b.booking_reference}</td>
                    <td className="px-4 py-3 text-slate-600">{b.booking_date}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{b.start_time}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{b.customer_name}</td>
                    <td className="px-4 py-3 text-slate-600">{b.activity_name}</td>
                    <td className="px-4 py-3 text-slate-600">{b.participants_count}</td>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{formatMoney(b.total_amount_cents, b.currency)}</td>
                    <td className="px-4 py-3"><PaymentBadge status={b.payment_status} /></td>
                    <td className="px-4 py-3"><BookingStatusBadge status={b.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{SOURCES[b.source] || b.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Cards (mobile) */}
            <div className="divide-y divide-slate-100 md:hidden">
              {bookings.map((b) => (
                <button key={b.id} onClick={() => setDetailsId(b.id)} data-testid={`booking-card-${b.id}`} className="flex w-full flex-col gap-1.5 p-4 text-left hover:bg-slate-50">
                  <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold text-slate-900">{b.booking_reference}</span><span className="font-mono text-sm font-bold text-slate-900">{formatMoney(b.total_amount_cents, b.currency)}</span></div>
                  <p className="text-sm font-medium text-slate-800">{b.customer_name} · {b.activity_name}</p>
                  <p className="text-xs text-slate-500">{b.booking_date} · {b.start_time} · {b.participants_count} pax</p>
                  <div className="mt-1 flex gap-2"><PaymentBadge status={b.payment_status} /><BookingStatusBadge status={b.status} /></div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{page * limit + 1}–{Math.min((page + 1) * limit, total)} sur {total}</span>
          <div className="flex gap-1.5">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={(page + 1) * limit >= total} onClick={() => setPage((p) => p + 1)} data-testid="next-page" className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <BookingFormDrawer open={formOpen} onOpenChange={setFormOpen} booking={editing} onSaved={() => setEditing(null)} />
      <BookingDetailsDrawer bookingId={detailsId} open={!!detailsId} onOpenChange={(o) => { if (!o) { setDetailsId(null); params.delete("open"); setParams(params); } }} onEdit={(b) => { setDetailsId(null); setEditing(b); setFormOpen(true); }} />
    </div>
  );
}
